/**
 * Makes every other child of the gate's container inert while the "Update required" layer is up (kzd-ac review MV-4).
 *
 * `inert` on `ion-split-pane` alone left out what Ionic puts beside it: a modal, an alert, a PIN pad or a toast opened
 * BEFORE the gate rose is a child of `ion-app` (Ionic appends its overlays to the app root), so it kept keyboard focus
 * under the layer. Here every child of the container but `keep` is inert, and so is one added later (a toast or an
 * overlay opened while the gate is up), watched with a `MutationObserver`. Nothing is dismissed or removed — a sale in a
 * modal is still there, untouched, once a newer build replaces this one; inert only takes away focus, clicks and keys.
 *
 * Undo (the returned function, called when the layer goes) lifts `inert` only from the elements this set it on: one that
 * was inert already, for its own reason, stays as it was.
 */
export function inertSiblings(keep: HTMLElement, container: HTMLElement | null = keep.parentElement): () => void {
  if (!container) {
    return () => undefined;
  }
  const mine = new Set<Element>();
  const quiet = (el: Element): void => {
    if (el === keep || el.hasAttribute('inert')) {
      return;
    }
    el.setAttribute('inert', '');
    mine.add(el);
  };
  Array.from(container.children).forEach(quiet);
  const watch =
    typeof MutationObserver === 'undefined'
      ? null
      : new MutationObserver((records) => {
          for (const r of records) {
            r.addedNodes.forEach((n) => {
              if (n.nodeType === 1 && n.parentElement === container) quiet(n as Element);
            });
          }
        });
  watch?.observe(container, { childList: true });
  return () => {
    watch?.disconnect();
    mine.forEach((el) => el.removeAttribute('inert'));
    mine.clear();
  };
}
